import type Experiments from '@wix/wix-experiments';
import isFunction from 'lodash/isFunction';
import {
  type OnPaymentAuthorized,
  type OnShippingMethodSelected,
  type OnShippingContactSelected,
  type GetInitialPaymentData,
} from '../../types/ExternalContract';
import { type StartApplePayFlowParams } from '../../actions/payment/startApplePayFlow';
import { type WidgetMeta } from '../../types/WidgetProps';
import { type AuthParams } from '../../types/AuthParams';
import { type BiLogger } from '../../components/Provider/BiLogger/BiLogger';
import { type ApplePayPaymentRequestParams, createApplePayPaymentRequest } from './applePayPaymentRequest';
import { getApplePayVersionToUse } from './getApplePayVersionToUse';
import { validateMerchantHandler } from './validateMerchantHandler';
import { shippingContactSelectedHandler } from './shippingContactSelectedHandler';
import { shippingMethodSelectedHandler } from './shippingMethodSelectedHandler';
import { paymentAuthorizeHandler } from './paymentAuthorizedHandler';
import { cancelHandler } from './cancelHandler';
import { createApplePayError } from './createApplePayError';
import { paymentMethodSelectedHandler } from './paymentMethodSelectedHandler';

export interface ApplePayServiceProps {
  domain: string;
  requestShipping: boolean;
  meta: WidgetMeta;
  sessionId: string;
  onShippingContactSelected: OnShippingContactSelected;
  onShippingMethodSelected: OnShippingMethodSelected;
  onPaymentAuthorized: OnPaymentAuthorized;
  getInitialPaymentDataWithBi?: GetInitialPaymentData;
  biLogger: BiLogger;
  experiments: Experiments;
}

export type InitSessionParams = ApplePayPaymentRequestParams & ApplePayServiceProps;

/**
 * Initializes Apple Pay Payment Request and create Apple Pay Session
 */
export const initApplePaySession = async (
  {
    meta,
    sessionId,
    countryCode,
    currencyCode,
    domain,
    paymentLabel,
    paymentAmount,
    paymentBreakdown,
    shippingMethods,
    merchantCapabilities,
    supportedNetworks,
    requestShipping,
    biLogger,
    fedopsLogger,
    experiments,
    onShippingContactSelected,
    onShippingMethodSelected,
    onPaymentAuthorized,
    getInitialPaymentDataWithBi,
  }: StartApplePayFlowParams,
  auth: AuthParams,
) => {
  /**
   * Creating ApplePaySession and handling callbacks should be in sync
   * between Thunderbolt implementation (https://github.com/wix-private/thunderbolt/blob/master/packages/feature-apple-pay/src/tpaHandlers.ts#L68, "./initApplePaySessionJsSdk.ts")
   * for in-iframe solutions (e.g. Ecom Checkout page),
   * and cases when e-widget is in the top window (e.g. Ecom Product page, Ecom Cart page) "./initApplePaySession"
   */

  const request = createApplePayPaymentRequest({
    countryCode,
    currencyCode,
    paymentLabel,
    paymentAmount,
    paymentBreakdown,
    shippingMethods,
    merchantCapabilities,
    supportedNetworks,
    requestShipping,
    experiments,
  });

  const session: ApplePaySession = new window.ApplePaySession(getApplePayVersionToUse(), request);

  session.onvalidatemerchant = async (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
    const result = await validateMerchantHandler({ domain, meta }, biLogger, auth, fedopsLogger)(event);
    session.completeMerchantValidation(result);
  };

  if (isFunction(getInitialPaymentDataWithBi) && !requestShipping) {
    // invoke getInitialPaymentData only for digital goods
    session.onpaymentmethodselected = async (event: ApplePayJS.ApplePayPaymentMethodSelectedEvent) => {
      const result = await paymentMethodSelectedHandler(
        getInitialPaymentDataWithBi,
        {
          appInstanceId: meta.appInstanceId,
          currencyCode,
          paymentLabel,
        },
        biLogger,
      )(event);
      session.completePaymentMethodSelection(result);
    };
  }

  session.onshippingcontactselected = async (event: ApplePayJS.ApplePayShippingContactSelectedEvent) => {
    const result = await shippingContactSelectedHandler(
      onShippingContactSelected,
      {
        meta,
        currencyCode,
        paymentLabel,
        paymentAmount,
      },
      biLogger,
      getInitialPaymentDataWithBi,
    )(event);
    if (Array.isArray(result.errors)) {
      result.errors = result.errors.map((err) => createApplePayError(err));
    }
    session.completeShippingContactSelection(result);
  };

  session.onshippingmethodselected = async (event: ApplePayJS.ApplePayShippingMethodSelectedEvent) => {
    try {
      const r = await shippingMethodSelectedHandler(
        onShippingMethodSelected,
        {
          paymentLabel,
          appInstanceId: meta.appInstanceId,
          currencyCode,
        },
        biLogger,
      )(event);
      session.completeShippingMethodSelection(r);
    } catch (e) {
      session.abort();
    }
  };

  session.onpaymentauthorized = async (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
    const r = await paymentAuthorizeHandler(
      onPaymentAuthorized,
      { shippingMethods, appInstanceId: meta.appInstanceId, msid: meta.siteId, currencyCode, sessionId },
      biLogger,
      auth,
    )(event);
    session.completePayment(r);
  };

  session.oncancel = cancelHandler({
    biLogger,
    biData: {
      paymentAmount,
      currencyCode,
    },
  });

  return session;
};
